import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";

import { AccessMode, ViewMode } from "@superblocksteam/shared";
import type { TokenVaultSeedRequest } from "@superblocksteam/shared";

import { startTokenVaultSeed } from "core/auth/tokenVaultSeed";

import TokenProvider from "../../auth/token";
import { getAppViewMode } from "../../legacy/selectors/applicationSelectors";
import { getAccessMode } from "../../legacy/selectors/usersSelectors";
import { GENERATE_TOKEN } from "../../pages/routes";
import { HttpMethod, callServer } from "../../store/utils/client";

export const isExpired = (jwt: string) => {
  try {
    const decoded = JSON.parse(atob(jwt.split(".")[1]));
    return decoded.exp < Date.now() / 1000;
  } catch {
    return false;
  }
};

function useJwt(): [string | undefined, () => Promise<void>, boolean] {
  const [loading, setLoading] = useState(true);
  const [storedToken, setStoredToken] = useState(() =>
    TokenProvider.getToken(),
  );
  const accessMode = useSelector(getAccessMode);
  const { connectAccountWithRedirect } = useAuth0();
  const appViewMode = useSelector(getAppViewMode);
  const tokenViewMode = appViewMode ?? ViewMode.DEPLOYED;
  const viewModeRef = useRef<ViewMode | null>(tokenViewMode);

  const fetchJwt = useCallback(async () => {
    if (accessMode === AccessMode.EXTERNAL_USER) {
      // already have the JWT
      return;
    } else if (accessMode === AccessMode.VISITOR) {
      // don't need a JWT
      return;
    }
    try {
      const response = await callServer<{
        access_token: string;
        token_vault_seed?: TokenVaultSeedRequest;
      }>(
        {
          method: HttpMethod.Post,
          url: GENERATE_TOKEN,
          body: {
            view_mode: tokenViewMode,
          },
        },
        {
          notifyOnError: false,
          shouldCrashApp: false,
        },
      );
      if (response.access_token) {
        TokenProvider.setToken(response.access_token);
        setStoredToken(response.access_token);
      }
      if (response.token_vault_seed) {
        // Awaited, not fired and forgotten: this navigates the tab to the identity
        // provider, and the caller is still holding the loading state here. Firing
        // it off would let the app paint and become interactive first, so the
        // navigation would land a second or two later and read as the page
        // redirecting itself for no reason. Every step inside is time-bounded.
        await startTokenVaultSeed({
          connectAccount: connectAccountWithRedirect,
          seed: response.token_vault_seed,
        });
      }
    } catch (error) {
      console.error("Could not fetch JWT", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [accessMode, connectAccountWithRedirect, tokenViewMode]);

  useEffect(() => {
    TokenProvider.setFetchToken(fetchJwt);
    return () => {
      TokenProvider.setFetchToken(undefined);
    };
  }, [fetchJwt]);

  useEffect(() => {
    if (
      accessMode === AccessMode.EXTERNAL_USER ||
      accessMode === AccessMode.VISITOR
    ) {
      // in this case the jwt is passed to the embed or the user is a visitor, we should never fetch it
      setLoading(false);
      return;
    } else if (
      storedToken &&
      !isExpired(storedToken) &&
      viewModeRef.current === tokenViewMode
    ) {
      setLoading(false);
    } else {
      setLoading(true);
      viewModeRef.current = tokenViewMode;
      void fetchJwt().catch(() => {});
    }
  }, [fetchJwt, storedToken, accessMode, tokenViewMode]);

  return [storedToken, fetchJwt, loading];
}

export default useJwt;
